// stripe.service.ts
import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

import {
  EventTypeKey,
  CustomerEnvelope,
  CustomerResponse,
  PriceEnvelope,
  PriceResponse,
  PaymentIntentEnvelope,
  PaymentIntentResponse,
  SetupIntentEnvelope,
  SetupIntentResponse,
  SubscriptionEnvelope,
  SubscriptionResponse,
  InvoiceResponse,
  InvoiceEnvelope,
  StripeInvoiceLite,
} from '../types/stripe-models';
import { CardFeeType } from '../types/donation-flow.models';

import {
  CREZCO_DONATION_APP_CONFIG,
  CrezcoDonationAppRuntimeConfig,
} from 'src/app/app.config';

// Unión para la implementación
type AnyEnvelope =
  | CustomerEnvelope
  | PriceEnvelope
  | InvoiceEnvelope
  | PaymentIntentEnvelope
  | SetupIntentEnvelope
  | SubscriptionEnvelope;

@Injectable({ providedIn: 'root' })
export class StripeService {
  constructor(
    private http: HttpClient,
    @Inject(CREZCO_DONATION_APP_CONFIG)
    private cfg: CrezcoDonationAppRuntimeConfig
  ) {}

  private get base(): string {
    return `${this.cfg.apiBaseUrl}stripe`;
  }

  private endpointFor(type: EventTypeKey): string {
    switch (type) {
      case 'Customer':
        return `${this.base}/customer`;
      case 'Price':
        return `${this.base}/price`;
      case 'PaymentIntent':
        return `${this.base}/payment-intent`;
      case 'SetupIntent':
        return `${this.base}/setup-intent`;
      case 'Subscription':
        return `${this.base}/subscription`;
      case 'Invoice':
        return `${this.base}/invoice`;
    }
  }

  // Overloads tipados (retorno correcto por envelope)
  send(envelope: CustomerEnvelope): Promise<CustomerResponse>;
  send(envelope: PriceEnvelope): Promise<PriceResponse>;
  send(envelope: PaymentIntentEnvelope): Promise<PaymentIntentResponse>;
  send(envelope: SetupIntentEnvelope): Promise<SetupIntentResponse>;
  send(envelope: SubscriptionEnvelope): Promise<SubscriptionResponse>;
  send(envelope: InvoiceEnvelope): Promise<StripeInvoiceLite>;

  // Implementación enrutando por type
  async send(
    envelope: AnyEnvelope
  ): Promise<
    | CustomerResponse
    | PriceResponse
    | InvoiceResponse
    | StripeInvoiceLite
    | PaymentIntentResponse
    | SetupIntentResponse
    | SubscriptionResponse
  > {
    const url = this.endpointFor(envelope.type);
    return lastValueFrom(this.http.post(url, envelope)) as any;
  }

  getCardFeeType(paymentMethodId: string): Promise<CardFeeType> {
    const id = encodeURIComponent(paymentMethodId);

    return lastValueFrom(
      this.http.get<CardFeeType>(`${this.base}/${id}/card-fee-type`)
    );
  }
}
